import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Trophy, Users, ChevronDown, ExternalLink, Smartphone, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Norwegian month names
const MONTHS_NO = ['Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];
const DAYS_NO = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];

export const MatchCalendar = () => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchMatches();
  }, [showPast]);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/matches/teams`);
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const endpoint = showPast ? '/api/matches/all' : '/api/matches?upcoming=true';
      const response = await fetch(`${API_URL}${endpoint}`);
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = selectedTeam === 'all' 
    ? matches 
    : matches.filter(m => m.team_id === selectedTeam);

  // Group matches by date
  const groupedMatches = filteredMatches.reduce((groups, match) => {
    const date = match.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(match);
    return groups;
  }, {});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const dayName = DAYS_NO[date.getDay()];
    const day = date.getDate();
    const month = MONTHS_NO[date.getMonth()];
    const year = date.getFullYear();
    return `${dayName} ${day}. ${month} ${year}`;
  };

  const adultTeams = teams.filter(t => t.category === 'voksen');
  const youthTeams = teams.filter(t => t.category === 'ungdom');

  return (
    <div data-testid="match-calendar-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=1920&q=80"
            alt="Fotballkamp"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4">{t('matches.label')}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('matches.title1')}<br />
            <span className="text-green-400">{t('matches.title2')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            {t('matches.intro')}
          </p>
        </div>
      </section>

      {/* External Links Banner */}
      <section className="py-8 bg-white border-y border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('matches.findAll')}</h3>
              <p className="text-slate-500 dark:text-slate-400">
                {t('matches.findAllDesc')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a 
                href="https://www.fotball.no/turneringer/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:bg-slate-700">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Fotball.no
                </Button>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.MinFotball&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1&pli=1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-black text-white hover:bg-gray-800">
                  <Smartphone className="w-4 h-4 mr-2" />
                  Google Play
                </Button>
              </a>
              <a 
                href="https://apps.apple.com/no/app/minfotball/id515897035"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-black text-white hover:bg-gray-800">
                  <Smartphone className="w-4 h-4 mr-2" />
                  App Store
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-green-500" />
              <span className="text-slate-900 font-medium">{t('matches.filterByTeam')}</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setSelectedTeam('all')}
                className={selectedTeam === 'all' 
                  ? 'bg-green-500 text-black' 
                  : 'bg-slate-100 text-slate-900 hover:bg-zinc-700'}
              >
                {t('matches.allTeams')}
              </Button>
              
              {/* Adult teams */}
              {adultTeams.map(team => (
                <Button
                  key={team.id}
                  size="sm"
                  onClick={() => setSelectedTeam(team.id)}
                  className={selectedTeam === team.id 
                    ? 'bg-green-500 text-black' 
                    : 'bg-slate-100 text-slate-900 hover:bg-zinc-700'}
                >
                  {team.name}
                </Button>
              ))}
              
              {/* Youth teams dropdown could be added here */}
              {youthTeams.map(team => (
                <Button
                  key={team.id}
                  size="sm"
                  onClick={() => setSelectedTeam(team.id)}
                  className={selectedTeam === team.id 
                    ? 'bg-green-500 text-black' 
                    : 'bg-slate-100 text-slate-900 hover:bg-zinc-700'}
                >
                  {team.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Toggle past matches */}
          <div className="flex items-center gap-4 mb-8">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 bg-slate-100 text-green-500 focus:ring-green-500"
              />
              <span className="text-slate-500 dark:text-slate-400">{t('matches.showPast')}</span>
            </label>
          </div>
        </div>
      </section>

      {/* Matches List */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
              {showPast ? 'Alle kamper' : 'Kommende kamper'}
            </h2>
            <p className="text-slate-500 mt-2">
              {showPast 
                ? 'Oversikt over alle kamper med resultater' 
                : 'Se når lagene våre spiller neste gang'}
            </p>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-slate-100 rounded w-48 mb-4"></div>
                  <div className="h-20 bg-slate-100 rounded"></div>
                </div>
              ))}
            </div>
          ) : Object.keys(groupedMatches).length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <Calendar className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t('matches.noneFound')}</h3>
              <p className="text-slate-500 mb-6">
                {selectedTeam !== 'all' 
                  ? 'Det er ingen kommende kamper for dette laget.'
                  : 'Det er ingen kommende kamper registrert ennå.'}
              </p>
              <p className="text-slate-400 text-sm">
                {t('matches.check')} <a href="https://www.fotball.no/turneringer/" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">Fotball.no</a> {t('matches.forFullFixtures')}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedMatches).map(([date, dayMatches]) => (
                <div key={date}>
                  {/* Date header */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">{formatDate(date)}</h3>
                      <p className="text-slate-400 text-sm">{dayMatches.length} kamp{dayMatches.length > 1 ? 'er' : ''}</p>
                    </div>
                  </div>

                  {/* Matches for this date */}
                  <div className="space-y-3">
                    {dayMatches.map(match => (
                      <div 
                        key={match.id}
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:border-green-500/30 transition-colors"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          {/* Team info */}
                          <div className="flex items-center gap-4">
                            <div className={`w-2 h-12 rounded-full ${match.is_home ? 'bg-green-500' : 'bg-zinc-600'}`}></div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
                                  {match.team_name}
                                </span>
                                {match.competition && (
                                  <span className="text-xs font-medium px-2 py-0.5 bg-green-500/10 text-green-500 rounded">
                                    {match.competition}
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-900 font-bold text-lg">
                                {match.is_home 
                                  ? `Veitvet SK vs ${match.opponent}`
                                  : `${match.opponent} vs Veitvet SK`}
                              </p>
                            </div>
                          </div>

                          {/* Match details */}
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                              <Clock className="w-4 h-4" />
                              <span>{match.time}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                              <MapPin className="w-4 h-4" />
                              <span>{match.is_home ? 'Hjemme' : match.location || 'Borte'}</span>
                            </div>
                            {match.result && (
                              <div className="flex items-center gap-2">
                                <Trophy className="w-4 h-4 text-green-500" />
                                <span className="text-slate-900 font-bold">{match.result}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MinFotball App Promotion */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl border border-green-500/30 p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 bg-green-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-12 h-12 text-green-500" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('matches.downloadApp')}</h2>
                <p className="text-slate-500 mb-6">
                  Få alle kamper, resultater og terminlister rett i lomma. 
                  Følg Veitvet Sportsklubb og få varsler om kommende kamper.
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-4">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.MinFotball&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1&pli=1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-black text-white hover:bg-gray-800 border border-gray-700">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      Google Play
                    </Button>
                  </a>
                  <a 
                    href="https://apps.apple.com/no/app/minfotball/id515897035"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-black text-white hover:bg-gray-800 border border-gray-700">
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                      </svg>
                      App Store
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Link to Soccer page */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{t('matches.seeTeams')}</h2>
          <p className="text-slate-500 mb-8">
            {t('matches.seeTeamsDesc')}
          </p>
          <Link to="/soccer">
            <Button className="bg-green-500 text-black font-bold uppercase tracking-wider px-8 py-6 hover:bg-green-500/90">
              <Users className="mr-2 w-5 h-5" />
              {t('matches.goToFootball')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Grasrotandelen */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default MatchCalendar;
